import { EmptyDocument } from "component/flex/invoice/InvoiceEmptyState";
import { calculatePercentagePaid, formatDate } from "component/flex/utils";
import { Button } from "component/ui/Button";
import { displayCurrencyAndAmount, ExpectedStatus } from "constants/FinanceConstants";
import { financeStatus } from "constants/global";
import { DocumentText } from "iconsax-reactjs";
import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useState } from "react";
import { paymentStatusEnums } from "./ManageFinance";
import { useMutation } from "react-query";
import { downloadReceipt } from "api/credit-fetchers";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { formatFileUrl } from "component/finance/FIleUploader";
import ActionDropdown from "component/actionDropdown";
import QuestionHelpIcon from "component/QuestionHelpIcon";
import FinanceBadge from "component/finance/FinanceBadge";

const paymentReferenceType = {
  swiftReference: "Swift Reference",
  contractAgreement: "Contract Agreement"
}

const ManageFinanceDetails = ({
  payableFinance,
  contractUserInitials,
  contractUserFullName,
  contractUserEmail,
  currency,
  status,
  totalAmount,
  dataList,
  paymentTab,
  setMakeARepayment,
  setPaymentTab,
  loans,
  isReceivableFinance,
  isPaymentFinance,
  setAmount, setShowRepayCryptoModal, disableRepaymentButton, financeDetails
}) => {
  const { id: financeId } = useParams();
  const [downloadIndexLoading, setDownloadIndexLoading] = useState(null);

  const dataToShow = isReceivableFinance
    ? payableFinance?.invoices
    : payableFinance?.purchaseOrders;
  const tabToShow = status === "pending" ? ["Documents"] : ["Re-payments", "Documents"];

  const nextAmountDue = loans?.find(
    (item) => item?.paymentStatus === paymentStatusEnums.UNPAID
  )?.amount;

  const { mutateAsync: handleDownloadReceipt, isLoading: isDownloadingReceipt } = useMutation({
    mutationFn: (subLoanId) => downloadReceipt({ financeId, subLoanId }),
    onSettled: (response) => {
      if (response?.status === 404) {
        toast?.error(response?.response?.data?.message);
      } else {
        toast?.success(response?.response?.data?.message);
      }
      setDownloadIndexLoading(null);
    },
  });

  const paidAmountFromLoan = loans
    ?.filter((item) => item?.paymentStatus === paymentStatusEnums.REPAID)
    ?.reduce((acc, cur) => (acc += cur?.amount), 0);
  const hasDocuments = payableFinance?.uploadDocuments ?? {};
  const documentKeys = {
    contractAgreement: "Contract Agreement",
    transactionUrl: "Transaction Document",
    freightPaymentUrl: "Freight Document",
    transactionDocument: "Transaction Document",
    freightDocument: "Freight Document",
    bankStatement: "Bank Statement",
    boardResolution: "Board Resolution",
  };

  const isPaymentDocumentToShow = payableFinance?.paymentReferenceType === "swiftReference" && payableFinance?.swiftReference?.swiftType !== "UETR" ? payableFinance?.swiftReference?.link : payableFinance?.contractAgreement;
  const repaymentDoc = financeDetails?.repayments;

  const isPaymentShowRepaymentNotLoan = financeDetails?.repayments?.map(item => ({
    ...item,
    amount: totalAmount,
    paymentStatus: item?.status
  })) ?? loans;

  return (
    <div className="w-full md:w-7/12">
      {payableFinance?.contractEmployer && payableFinance?.contractEmployer?.email && (
        <>
          <p className="pb-1 mt-5 text-sm font-[450] ">Contract Employer</p>
          <div className="mb-6 bg-white rounded-xl">
            <div className="flex justify-between items-center !px-5 !py-4">
              <div className="flex items-center justify-start gap-2">
                <div className="flex items-center justify-center w-10 h-10 font-semibold text-[#475467] bg-[#EFF0FB] rounded-full">
                  {contractUserInitials}
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-medium mb-0 !text-left capitalize text-gray-90">
                    {contractUserFullName}
                  </h3>
                  <p className="text-xs font-light text-secondary-40">
                    {contractUserEmail}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {dataToShow?.length ? (
        <>
          <p className="pb-1 mt-2 text-sm font-[450] ">
            {!isReceivableFinance ? "Purchase order" : "Invoice"}
          </p>

          {dataToShow?.map((item, index) => (
            <div key={index} className="mb-6 bg-white rounded-xl">
              <div className="flex flex-col !px-4 gap-1 !py-2">
                <div className="flex items-center justify-between w-full text-gray-60">
                  <p className="text-sm font-medium ">{item?.title}</p>
                  <p className="font-medium text-md">
                    {displayCurrencyAndAmount(currency, item?.totalAmount)}{" "}
                  </p>
                </div>
                <div className="flex items-center justify-between w-full">
                  <p className="text-sm font-medium ">
                    <span
                      style={{
                        color: financeStatus[item?.orderStatus]?.textColor,
                      }}
                    >
                      {financeStatus[item?.orderStatus]?.text}
                    </span>{" "}
                    •{" "}
                    <span className="text-main-grey">
                      {formatDate(item?.createdAt)}
                    </span>
                  </p>
                  <p className="text-xs !font-light text-main-grey ">
                    {item?.orderItems?.length} Items
                  </p>
                </div>
              </div>
            </div>
          ))}
        </>
      ) : null}
      <>
        <p className="pb-1 mt-2 text-sm font-[450] ">Loan Details</p>
        <div className="p-3 py-4 mb-6 bg-white rounded-xl">
          {status === ExpectedStatus.Disbursed && (
            <div className="mb-6">
              <p className="text-sm font-light text-secondary-40">{isPaymentFinance ? "Amount due" : "Next due"}</p>
              <p className="text-2xl font-normal text-gray-90">
                {displayCurrencyAndAmount(currency, isPaymentFinance ? totalAmount : nextAmountDue)}
              </p>
            </div>
          )}

          {!isPaymentFinance ? <div style={{ border: "1px solid" }} className="rounded-lg !border-light-grey">
            <div className="border-b border-light-grey">
              {status === ExpectedStatus.Disbursed && (
                <div className="!p-5">
                  <p className="text-sm flex text-[#475467]">
                    <span className="font-normal text-gray-70">
                      Payment progress
                    </span>
                    <span className="ml-auto font-light text-secondary-40">
                      {calculatePercentagePaid(
                        totalAmount,
                        paidAmountFromLoan
                      )}
                      % complete
                    </span>
                  </p>
                  <div className="bg-[#EAECF0] rounded-[20px] h-2 !my-3.5 relative">
                    <div
                      className="bg-[#11A811] absolute left-0 top-0 h-full rounded-[20px] transition-[width]"
                      style={{
                        width: `${calculatePercentagePaid(
                          totalAmount,
                          paidAmountFromLoan
                        )}%`,
                      }}
                    />
                  </div>
                </div>
              )}
              {status === ExpectedStatus.Disbursed && (
                <hr
                  style={{ margin: "0px" }}
                  className="border-b-[2px] border-[#EBEDF9]"
                />
              )}
              <div className="grid grid-cols-4 gap-4 p-2 px-4">
                {dataList?.map(({ label, value }, index) => {
                  return (
                    <div key={index + label} className="">
                      <p className="text-sm font-light text-secondary-40">
                        {label}
                      </p>
                      <p className="text-sm font-medium text-gray-90">
                        {value}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div> :
            <div style={{ border: "1px solid" }} className="rounded-lg gap-3 !border-light-grey p-3">
              <div className="flex items-center justify-between mt-3">
                <div
                  className='w-1/2'>
                <p className="text-sm font-light text-secondary-40">
                  Purpose of funds
                </p>
                <p className="text-sm font-medium text-gray-90">
                  {payableFinance?.purposeOfFund || "N/A"}
                </p>
                </div>
                {payableFinance?.paymentReferenceType === "contractAgreement" && <div className='w-1/2'>
                  <p className="text-sm font-light text-secondary-40">
                    Payment reference
                  </p>
                  <p className="text-sm font-medium text-gray-90">
                    {paymentReferenceType[payableFinance?.paymentReferenceType]}
                  </p>
                </div>}
              </div>
              {payableFinance?.paymentReferenceType !== "contractAgreement" && (
                <>
                  <div className="flex items-center justify-between mt-3">
                    <div className='w-1/2'>
                      <p className="text-sm font-light text-secondary-40">
                        Payment reference
                      </p>
                      <p className="text-sm font-medium text-gray-90">
                        {paymentReferenceType[payableFinance?.paymentReferenceType]}
                      </p>
                    </div>
                    {(payableFinance?.swiftReference?.swiftType === 'UETR') && <div className='w-1/2'>
                      <p className="text-sm font-light text-secondary-40">
                        {payableFinance?.swiftReference?.swiftType === 'UETR' ? "UETR" : ""}
                      </p>
                      <p className="text-sm text-wrap whitespace-break-spaces font-medium text-gray-90">
                        {payableFinance?.swiftReference?.uetr
                          || "N/A"}
                      </p>
                    </div>}
                    {(payableFinance?.swiftReference?.swiftType) && <div className='w-1/2'>
                      <p className="text-sm font-light text-secondary-40">
                       Swift type
                      </p>
                      <p className="text-sm text-wrap whitespace-break-spaces font-medium text-gray-90">
                        {payableFinance?.swiftReference?.swiftType}
                      </p>
                    </div>}
                  </div>
                  {payableFinance?.swiftReference?.bankName && (
                    <div className="grid grid-cols-4 gap-4 mt-3">
                      <div>
                        <p className="text-sm font-light text-secondary-40">
                          Bank name
                        </p>
                        <p className="text-sm font-medium text-gray-90">
                          {payableFinance?.swiftReference?.bankName}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-light text-secondary-40">
                          Contact name
                        </p>
                        <p className="text-sm font-medium text-gray-90">
                          {payableFinance?.swiftReference?.contactPerson}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-sm font-light text-secondary-40">
                          Contact email
                        </p>
                        <p className="text-sm font-medium text-gray-90">
                          {payableFinance?.swiftReference?.contactEmail}
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}

              <div style={{ borderBottom: '1px solid' }}
                className="my-4 !border-[#EBEDF9]"
              />

              <div className="grid grid-cols-4 gap-4">
                {dataList?.map(({ label, value }, index) => {
                  return (
                    <div key={index + label} className="">
                      <p className="text-sm flex items-center gap-1  font-light text-secondary-40">
                        {label} {(isPaymentFinance && (label === 'Lateness fee' || label === 'Transfer fee')) && <QuestionHelpIcon
                          details={
                            <span>
                              {label === 'Lateness fee' ? "We charge a fee of 0.17%  daily on the overdue amount" : `You will be charged a fee of ~ 1 ${currency || 'USDC'} to complete this transfer`}</span>
                          }
                        />}
                      </p>
                      <p className="text-sm font-medium text-wrap break-all break-words whitespace-break-spaces text-gray-90">
                        {value}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>}
        </div>
      </>

      <>
        <div className="flex !mt-7 gap-4 md:!gap-8 px-3 md:!px-6 overflow-auto  [scrollbar-width:none] md:overflow-hidden">
          {tabToShow.map((tab) => {
            return (
              <span
                className={`!pb-2 !border-b-2 font-normal cursor-pointer transition-all  whitespace-nowrap duration-200 ${tab === paymentTab && "flex items-center !gap-2"
                  } ${tab === paymentTab
                    ? "font-[450] !border-secondary text-main-dark"
                    : "!border-transparent text-main-grey"
                  }`}
                style={{
                  borderBottom: tab === paymentTab ? "2px solid #6F00FF" : "none",
                  color: tab === paymentTab ? "text-main-dark" : "text-main-grey",
                  fontWeight: tab === paymentTab ? "450" : "400",
                }}
                key={tab}
                onClick={() => setPaymentTab(tab)}
              >
                {tab}
              </span>
            );
          })}
        </div>
        <div className="p-3 mb-6 bg-white rounded-xl">
          {paymentTab === "Re-payments" && (
            <div className="flex flex-col gap-2">
              {!isPaymentShowRepaymentNotLoan?.length ? (
                <EmptyDocument text="You have not made any repayment yet" />
              ) : (
                  isPaymentShowRepaymentNotLoan?.map((loan, index) => (
                  <div
                    key={index}
                    style={{ border: "1px solid #F2F3FC" }}
                    className="flex items-center justify-between px-3 py-2 rounded-lg"
                  >
                    <div>
                        <p className="text-base font-normal gap-3 text-gray-90">
                          <span className="pr-3">{displayCurrencyAndAmount(currency, loan?.amount)} </span> {disableRepaymentButton ? <FinanceBadge badgeType={loan?.status || loan?.paymentStatus} /> : null}
                      </p>
                      <p className="text-sm font-light text-secondary-40">
                        Due on {formatDate(loan?.dueDate)}
                      </p>
                    </div>

                    {isPaymentFinance ?
                      <ActionDropdown
                        dropClassName={'w-full'}
                        element={<Button
                          size="sm"
                          variant="ghost"
                          isDisabled={loan?.paymentStatus !== 'REJECT'}
                        >Make a repayment  <ChevronDown size={18} /> </Button>}
                        options={[
                          {
                            label: "Pay with your Tradevu wallet",
                            action: () => {
                              if (loan?.paymentStatus !== 'REJECT') return;
                              setMakeARepayment(true);
                              const amountToPrefill = payableFinance?.loans?.find(
                                (item) => item?.paymentStatus === "REPAID"
                              )?.amount;
                              setAmount(amountToPrefill);
                            },
                            closeAfterClick: true,
                          },
                          {
                            label: "Pay to a wallet address",
                            action: () => {
                              if (loan?.paymentStatus !== 'REJECT') return;
                              setShowRepayCryptoModal(true);
                            },
                            closeAfterClick: true,
                          },
                        ]} /> : <Button
                          onClick={() => {
                            if (
                              loan?.paymentStatus ===
                              paymentStatusEnums.REPAID
                            ) {
                              handleDownloadReceipt(loan?._id);
                          setDownloadIndexLoading(index);
                          return;
                        } else {
                          setMakeARepayment(true);
                        }
                      }}
                      size="sm"
                      variant="ghost"
                      disabled={
                        downloadIndexLoading === index ||
                        isDownloadingReceipt
                      }
                    >
                      {downloadIndexLoading === index && isDownloadingReceipt
                        ? "Loading..."
                        : loan?.paymentStatus === paymentStatusEnums.REPAID
                          ? "Download receipt"
                          : "Make a payment"}
                      </Button>}

                  </div>
                ))
              )}
            </div>
          )}
          {paymentTab === "Documents" && (
            isPaymentFinance ? <>
              <div className="flex flex-col gap-2 min-h-[150px]">
                {status === ExpectedStatus.Draft ? (
                  <EmptyDocument />
                ) : (!isPaymentDocumentToShow) ? (
                  <EmptyDocument />
                ) : <div className="flex flex-col gap-4">
                  <div
                    style={{ border: "1px solid #F2F3FC" }}
                    onClick={() => {
                      window.open(isPaymentDocumentToShow?.url, "_blank");
                    }}
                    className="flex flex-col items-start justify-between p-3 capitalize rounded-lg cursor-pointer"
                  >
                    <p className='flex items-center gap-2'>
                      <DocumentText />{" "}
                      <span className="text-base font-normal text-gray-90">
                        {isPaymentDocumentToShow?.name}
                      </span>
                    </p>

                    <p className="flex w-full text-sm font-light text-secondary-40">
                      <p className="w-full truncate">
                        {formatFileUrl(isPaymentDocumentToShow?.url)}
                      </p>
                      <ChevronRight className="pl-2" />
                    </p>
                  </div>
                      {disableRepaymentButton && repaymentDoc.map((item, index) => (<div
                        key={index}
                        style={{ border: "1px solid #F2F3FC" }}
                        onClick={() => {
                          window.open(item?.document?.url, "_blank");
                        }}
                        className="flex flex-col items-start justify-between p-3 capitalize rounded-lg cursor-pointer"
                      >
                        <p className='flex items-center gap-2'>
                          <DocumentText />{" "}
                          <span className="text-base truncate font-normal text-gray-90">
                            {formatFileUrl(item?.document?.url || '')}
                          </span>
                        </p>

                        <p className="flex w-full text-sm font-light text-secondary-40">
                          <p className="w-full truncate">
                            {formatFileUrl(item?.document?.url || '')}
                          </p>
                          <ChevronRight className="pl-2" />
                        </p>
                      </div>))}
                </div>}
              </div>
            </> : <div className="flex flex-col gap-2 min-h-[150px]">
              {status === ExpectedStatus.Draft ? (
                <EmptyDocument />
              ) : (!Object.entries(hasDocuments).length) ? (
                <EmptyDocument />
              ) : (
                <div className="flex flex-col gap-4">
                  {Object.entries(hasDocuments)?.map(([key, document], index) => (
                    <div
                      key={index}
                      style={{ border: "1px solid #F2F3FC" }}
                      onClick={() => {
                        window.open(document?.url, "_blank");
                      }}
                      className="flex items-center justify-between p-3 capitalize rounded-lg cursor-pointer"
                    >
                      <p>
                        <DocumentText />{" "}
                        <span className="text-base font-normal text-gray-90">
                          {documentKeys[key]}
                        </span>
                      </p>

                      <p className="text-sm font-light text-secondary-40">
                        {formatFileUrl(document?.url)}
                        <ChevronRight className="pl-2" />
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </>
    </div>
  );
};

export default ManageFinanceDetails;
